import React, { useState, useMemo } from 'react';
import useGameStore from '../store/gameStore';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '../store/stableEmpties';
import { CalendarDays, Lock, ClipboardList, GraduationCap, Sun, Wrench, Package, ArrowLeftRight, Users, ChevronLeft, ChevronRight, Truck } from 'lucide-react';
import { computeReturnDate } from '../components/hr/driverDetail/parts';

/**
 * Vue Agenda — Lot 5 (26/06/2026)
 *
 * Page conditionnée à la présence de DEUX fonctions support :
 *   1. RH (Nadia Cheikh, `role-nadia-cheikh`)
 *   2. Responsable d'exploitation (Rémi Forni, `role-christophe-faure`)
 *
 * Sans ces deux recrues, l'écran est verrouillé avec un appel à
 * l'action vers `/fonctions-support`. Avec les deux, on agrège toutes
 * les indispos sur 30 jours glissants :
 *   • Formations (driver.status === 'training')
 *   • Congés (driver.onHoliday)
 *   • Maintenance (vehicle.status === 'maintenance')
 *   • Livraisons en cours (vehicle.status === 'delivery')
 *   • Transferts (vehicle.status === 'transfer')
 *
 * Le joueur peut basculer en vue **semaine** (7 jours) ou **mois** (30 j)
 * et naviguer dans le temps avec ←/→.
 */

const VIEWS = { WEEK: 'week', MONTH: 'month' };

function dayPlus(day, month, year, offset) {
  let d = day + offset, m = month, y = year;
  while (d > 30) { d -= 30; m += 1; }
  while (d < 1) { d += 30; m -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1) { m += 12; y -= 1; }
  return { day: d, month: m, year: y };
}

function fmtDate(day, month) {
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
}

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function Agenda() {
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const calDay = useGameStore(s => s.day);
  const calMonth = useGameStore(s => s.month);
  const calYear = useGameStore(s => s.year);

  const hasHR = !!hiredSupport['role-nadia-cheikh'];
  const hasExploit = !!hiredSupport['role-christophe-faure'];
  const unlocked = hasHR && hasExploit;

  const [view, setView] = useState(VIEWS.WEEK);
  const [offsetDays, setOffsetDays] = useState(0); // navigation horizontale

  const horizon = view === VIEWS.WEEK ? 7 : 30;

  // Agrégation des indispos.
  const items = useMemo(() => {
    if (!unlocked) return [];
    const list = [];
    drivers.forEach(d => {
      if (d.status === 'training' && d.trainingEndDay) {
        list.push({
          kind: 'training',
          icon: GraduationCap,
          color: 'text-accent',
          bg: 'bg-accent/15',
          label: `${d.name} — Formation`,
          startDay: d.trainingStartDay || totalDays, // fallback prudent
          endDay: d.trainingEndDay,
          who: d.name,
        });
      }
      if (d.onHoliday && d.holidayEndDay) {
        list.push({
          kind: 'holiday',
          icon: Sun,
          color: 'text-emerald-300',
          bg: 'bg-emerald-500/15',
          label: `${d.name} — Congés`,
          startDay: d.holidayStartDay || totalDays,
          endDay: d.holidayEndDay,
          who: d.name,
        });
      }
    });
    vehicles.forEach(v => {
      const lbl = `${v.brand} ${v.model}`;
      if (v.status === 'maintenance' && v.maintenanceUntil) {
        list.push({
          kind: 'maintenance', icon: Wrench, color: 'text-warn', bg: 'bg-warn/15',
          label: `${lbl} — Réparation`,
          startDay: totalDays, endDay: v.maintenanceUntil, who: lbl,
        });
      }
      if (v.status === 'delivery' && v.availableOnDay) {
        list.push({
          kind: 'delivery', icon: Package, color: 'text-blue-300', bg: 'bg-blue-500/15',
          label: `${lbl} — Livraison concession`,
          startDay: totalDays, endDay: v.availableOnDay, who: lbl,
        });
      }
      if (v.status === 'transfer' && v.transferArrivalDay) {
        list.push({
          kind: 'transfer', icon: ArrowLeftRight, color: 'text-sky-300', bg: 'bg-sky-500/15',
          label: `${lbl} — Transfert inter-dépôts`,
          startDay: totalDays, endDay: v.transferArrivalDay, who: lbl,
        });
      }
      if (v.status === 'selling' && v.saleCompletionDay) {
        list.push({
          kind: 'selling', icon: Truck, color: 'text-purple-300', bg: 'bg-purple-500/15',
          label: `${lbl} — Vente en cours`,
          startDay: totalDays, endDay: v.saleCompletionDay, who: lbl,
        });
      }
    });
    return list;
  }, [unlocked, drivers, vehicles, totalDays]);

  // Filtre items qui chevauchent la fenêtre [start, start+horizon).
  const windowStart = totalDays + offsetDays;
  const windowEnd = windowStart + horizon;
  const visibleItems = useMemo(
    () => items.filter(it => it.endDay > windowStart && it.startDay < windowEnd),
    [items, windowStart, windowEnd],
  );

  // En-têtes des colonnes (1 colonne par jour).
  const headers = useMemo(() => {
    const out = [];
    for (let i = 0; i < horizon; i++) {
      const dayIdx = totalDays + offsetDays + i;
      const cal = dayPlus(calDay, calMonth, calYear, offsetDays + i);
      out.push({ dayIdx, ...cal });
    }
    return out;
  }, [horizon, totalDays, offsetDays, calDay, calMonth, calYear]);

  if (!unlocked) {
    return (
      <div className="space-y-4" data-testid="agenda-locked">
        <header className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-accent" />
          <h1 className="text-xl font-bold text-foreground">Agenda — Vue d&apos;ensemble</h1>
        </header>

        <div className="surface-card p-6 border-dashed">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-amber-500/10">
              <Lock className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-foreground mb-2">Vue agenda verrouillée</h2>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                L&apos;agenda centralise toutes les indisponibilités de votre équipe (congés, formations, absences) sur 7 ou 30 jours glissants. Cette vue est <strong className="text-foreground">débloquée automatiquement</strong> dès que vous avez recruté les deux fonctions support nécessaires à sa supervision :
              </p>

              <div className="space-y-2.5 mb-4">
                <div
                  className={`flex items-center gap-3 p-3 rounded-md border ${hasHR ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-600 bg-ink-900'}`}
                  data-testid="agenda-prereq-hr"
                >
                  <Users className={`w-4 h-4 ${hasHR ? 'text-emerald-300' : 'text-slate-500'}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Nadia Cheikh — Responsable RH</p>
                    <p className="text-xs text-slate-500">Suivi des congés, formations et climat social</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${hasHR ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                    {hasHR ? 'Recrutée' : 'À recruter'}
                  </span>
                </div>

                <div
                  className={`flex items-center gap-3 p-3 rounded-md border ${hasExploit ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-600 bg-ink-900'}`}
                  data-testid="agenda-prereq-exploit"
                >
                  <ClipboardList className={`w-4 h-4 ${hasExploit ? 'text-emerald-300' : 'text-slate-500'}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Rémi Forni — Responsable exploitation</p>
                    <p className="text-xs text-slate-500">Planning d&apos;équipe, réceptions et organisation du magasin</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${hasExploit ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                    {hasExploit ? 'Recruté' : 'À recruter'}
                  </span>
                </div>
              </div>

              <a
                href="/fonctions-support"
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors"
                data-testid="agenda-cta-recruit"
              >
                Voir les fonctions support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="agenda-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-accent" />
          <h1 className="text-xl font-bold text-foreground">Agenda — Vue d&apos;ensemble</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-ink-600 rounded-md overflow-hidden">
            <button
              onClick={() => { setView(VIEWS.WEEK); setOffsetDays(0); }}
              className={`px-3 py-1.5 text-xs ${view === VIEWS.WEEK ? 'bg-accent text-white' : 'text-slate-400 hover:bg-ink-700/30'}`}
              data-testid="agenda-view-week"
            >
              Semaine
            </button>
            <button
              onClick={() => { setView(VIEWS.MONTH); setOffsetDays(0); }}
              className={`px-3 py-1.5 text-xs ${view === VIEWS.MONTH ? 'bg-accent text-white' : 'text-slate-400 hover:bg-ink-700/30'}`}
              data-testid="agenda-view-month"
            >
              Mois (30 j)
            </button>
          </div>
          <button
            onClick={() => setOffsetDays(o => o - horizon)}
            className="p-1.5 border border-ink-600 rounded-md text-slate-400 hover:bg-ink-700/30"
            data-testid="agenda-nav-prev"
            aria-label="Période précédente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setOffsetDays(0)}
            className="px-3 py-1.5 text-xs border border-ink-600 rounded-md text-slate-400 hover:bg-ink-700/30"
            data-testid="agenda-nav-today"
          >
            Aujourd&apos;hui
          </button>
          <button
            onClick={() => setOffsetDays(o => o + horizon)}
            className="p-1.5 border border-ink-600 rounded-md text-slate-400 hover:bg-ink-700/30"
            data-testid="agenda-nav-next"
            aria-label="Période suivante"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="surface-card p-4 overflow-x-auto">
        <p className="text-xs text-slate-500 mb-3">
          Période affichée : <strong className="text-slate-300">{fmtDate(headers[0]?.day, headers[0]?.month)} {MONTHS[(headers[0]?.month || 1) - 1]}</strong> → <strong className="text-slate-300">{fmtDate(headers[horizon - 1]?.day, headers[horizon - 1]?.month)} {MONTHS[(headers[horizon - 1]?.month || 1) - 1]} {headers[horizon - 1]?.year}</strong>
          {' · '}{visibleItems.length} indispo{visibleItems.length > 1 ? 's' : ''}
        </p>

        {/* Timeline grid */}
        <div className="min-w-[640px]">
          {/* Header row : days */}
          <div className="grid border-b border-ink-700 pb-1.5 mb-2" style={{ gridTemplateColumns: `200px repeat(${horizon}, 1fr)` }}>
            <div className="text-[10px] uppercase text-slate-500 font-medium pl-1">Ressource</div>
            {headers.map((h) => (
              <div
                key={`${h.year}-${h.month}-${h.day}`}
                className={`text-[10px] text-center ${(h.day === calDay && h.month === calMonth) ? 'text-accent font-bold' : 'text-slate-500'}`}
              >
                {h.day}
                {h.day === 1 && <span className="block text-[9px] text-slate-600">{MONTHS[h.month - 1]}</span>}
              </div>
            ))}
          </div>

          {/* Rows */}
          {visibleItems.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-6 text-center" data-testid="agenda-empty">
              Aucune indisponibilité prévue sur cette période. Tout est opérationnel.
            </p>
          ) : (
            visibleItems.map((it, idx) => {
              const Icon = it.icon;
              const startCol = Math.max(0, it.startDay - windowStart);
              const endCol = Math.min(horizon, it.endDay - windowStart);
              const span = Math.max(1, endCol - startCol);
              const returnDate = computeReturnDate(calDay, calMonth, calYear, it.endDay - totalDays);
              // 07/02/2026 — code review: clé stable basée sur (kind + who + endDay)
              // pour éviter les re-render incorrects quand la liste se réordonne
              // (ex. ajout d'une nouvelle indispo en cours de partie).
              const stableKey = `${it.kind}-${it.who}-${it.startDay}-${it.endDay}`;
              return (
                <div
                  key={stableKey}
                  className="grid items-center py-1 border-b border-ink-800/50 hover:bg-ink-700/20"
                  style={{ gridTemplateColumns: `200px repeat(${horizon}, 1fr)` }}
                  data-testid={`agenda-row-${it.kind}-${idx}`}
                >
                  <div className="flex items-center gap-1.5 pl-1 truncate">
                    <Icon className={`w-3 h-3 flex-shrink-0 ${it.color}`} />
                    <span className="text-[11px] text-slate-300 truncate" title={it.label}>{it.label}</span>
                  </div>
                  {Array.from({ length: horizon }).map((_, c) => {
                    const inBar = c >= startCol && c < startCol + span;
                    const isLeftEdge = c === startCol;
                    const isRightEdge = c === startCol + span - 1;
                    return (
                      <div
                        key={c}
                        className={`h-5 mx-0.5 ${inBar ? it.bg : ''} ${isLeftEdge ? 'rounded-l-sm' : ''} ${isRightEdge ? 'rounded-r-sm' : ''}`}
                        title={inBar ? `${it.label} — retour ${returnDate || `J${it.endDay}`}` : ''}
                      />
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Légende */}
      <div className="surface-card p-3">
        <p className="text-[10px] uppercase text-slate-500 font-medium mb-2">Légende</p>
        <div className="flex flex-wrap gap-3 text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent/15" /> Formation</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500/15" /> Congés</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-warn/15" /> Maintenance</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500/15" /> Livraison</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sky-500/15" /> Transfert</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-purple-500/15" /> Vente</span>
        </div>
        <p className="text-[10px] text-slate-500 italic mt-2">
          Vue alimentée par Nadia Cheikh (RH) + Rémi Forni (exploitation).
        </p>
      </div>
    </div>
  );
}
